<template>
  <component
    is="portal"
    v-bind="modalAttrs">
    <dialog
      class="container-modal sidebarmodal sidebarmodal-backdrop align-center grid grid-cols-12">
      <article
        v-click-outside="onCloseHandler"
        class="sidebarmodal-dialog"
        :class="modalClass"
        :data-test="dataTest">
        <div
          :class="modalContentClass"
          class="sidebarmodal-content tw-flex tw-flex-col tw-h-screen bg-core-primary-100">
          <slot name="sidebarmodal-header">
            <div
              v-if="header"
              class="sidebarmodal-header-wrapper tw-flex-none s-pl-5 s-pr-6 s-pt-5 s-pb-4 bg-core-primary-100">
              <ModalHeader
                :iconName="header.iconName"
                :dataTest="header.dataTest"
                :title="header.title"
                @close="onCloseHandler" />
            </div>
          </slot>
          <div class="sidebarmodal-body tw-flex-1 s-pl-5 s-pr-6 s-pt-4 s-pb-4">
            <transition name="modal-content">
              <slot
                v-if="!loading"
                name="body" />
            </transition>
            <transition
              name="fade"
              mode="out-in">
              <div
                v-if="loading"
                data-test="modal-loading"
                class="modal-loading">
                <Loading />
              </div>
            </transition>
          </div>
          <div
            :class="footerClass"
            class="sidebarmodal-footer tw-flex-none tw-px-6 tw-pb-6 tw-pt-4">
            <div class="buttons-center tw-flex tw-gap-4 tw-justify-center">
              <slot name="footer" />
            </div>
          </div>
        </div>
      </article>
    </dialog>
  </component>
</template>

<script setup lang='ts'>
import Loading from 'frontcore/components/loadings/Loading.vue'
import ModalHeader from 'frontcore/views/modals/ModalHeader.vue'
import { computed, onBeforeUnmount, onMounted, PropType } from 'vue'
import { PORTAL_LEVELS } from 'frontcore/constants/constants'
import { SidebarHeader } from 'frontcore/views/modals/types'

const props = defineProps({
  // Use this prop to add a class to the modal
  // Here you can use this to define the width of the modal through classes
  // Example : modalClass="col-start-2 col-span-10"
  modalClass: {
    type: String,
    required: false,
    default: 'col-start-1 col-span-12 sm:col-start-8 sm:col-span-5 xxl:col-start-9 xxl:col-span-4',
  },
  portalTarget: {
    type: String,
    default: PORTAL_LEVELS.ONE,
  },
  header: {
    type: Object as unknown as PropType<SidebarHeader>,
    default: null,
  },
  footerClass: {
    type: String,
    required: false,
  },
  modalContentClass: {
    type: String,
    required: false,
  },
  dataTest: {
    type: String,
  },
  loading: {
    type: Boolean,
    default: false,
  },
})

const onCloseHandler = () => {
  const header = props.header
  if (header?.onCloseHandler !== undefined && !props.loading) {
    header.onCloseHandler()
  }
}

const modalAttrs = computed(() => {
  return {
    class: 'modal-native',
    to: props.portalTarget,
    tag: 'div',
  }
})

onMounted(() => {
  document.body.classList.add('overflow-hidden')
})

onBeforeUnmount(() => {
  document.body.classList.remove('overflow-hidden')
})
</script>
